import { colorToRGBA, getColorAtPixel, setColorAtPixel } from 'q-floodfill';
import IBounding from '../types/IBounding';
import ICoordinates from '../types/ICoordinates';
import addPaddingToBoundaries from './addBoundariesPadding';
import floodFill from './floodFill';
import getEmptyPlots from './getEmptyPlots';
import isBlack from './isBlack';

const backgroundColor = 'rgba(0, 0, 0, 0)';

const autoFillShapes = (
  canvas: HTMLCanvasElement,
  bounding: IBounding,
): {
  foundEmpty: boolean;
  coords: ICoordinates[];
  imgData: ImageData;
  left: number;
  top: number;
  width: number;
  height: number;
} | null => {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return null;
  }
  const padding = 20;
  const { top, left, right, bottom } = addPaddingToBoundaries(
    bounding,
    padding,
    canvas.width,
    canvas.height,
  );
  const width = right - left - 1;
  const height = bottom - top - 1;
  if (top === Infinity || left === Infinity) {
    return null;
  }
  const imgData = ctx.getImageData(left, top, width, height);
  const plots = getEmptyPlots(imgData, width, height);
  plots.forEach(({ x, y }) => {
    floodFill(imgData, '#000', x, y);
  });

  let foundEmpty = false;
  const currentLine = ctx.lineWidth;
  ctx.lineWidth = 4;
  const coords: ICoordinates[] = [];
  let lastPoint: ICoordinates | null = null;
  let hasActivePath = false;
  const maxSegmentGap = 3;
  for (let j = 0; j < height; j += 1) {
    for (let i = 0; i < width; i += 1) {
      const currentColor = getColorAtPixel(imgData, i, j);
      const isCurrentBlack = isBlack(currentColor);

      if (currentColor.a === 0) {
        const nextPoint = { x: i + left, y: j + top };
        const isNearby =
          !!lastPoint &&
          Math.abs(nextPoint.x - lastPoint.x) <= maxSegmentGap &&
          Math.abs(nextPoint.y - lastPoint.y) <= maxSegmentGap;
        if (!hasActivePath) {
          ctx.beginPath();
          ctx.moveTo(nextPoint.x, nextPoint.y);
          hasActivePath = true;
        } else if (isNearby) {
          ctx.lineTo(nextPoint.x, nextPoint.y);
        } else {
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(nextPoint.x, nextPoint.y);
        }
        foundEmpty = true;
        coords.push(nextPoint);
        lastPoint = nextPoint;
      } else if (currentColor.a !== 0 && !isCurrentBlack) {
        ctx.clearRect(i + left, j + top, 1, 1);
      }
      if (isCurrentBlack) {
        setColorAtPixel(imgData, colorToRGBA(backgroundColor), i, j);
      }
    }
  }
  if (foundEmpty && hasActivePath) {
    ctx.stroke();
  } else {
    ctx.putImageData(imgData, left, top, 0, 0, width, height);
  }
  ctx.lineWidth = currentLine;
  return {
    foundEmpty,
    coords,
    imgData: ctx.getImageData(left, top, width, height),
    left,
    top,
    width,
    height,
  };
};

export default autoFillShapes;
